import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, FileDown, FileSpreadsheet, LoaderCircle, MapPin, Moon, Printer, Sun } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { AppShell } from "@/components/AppShell";
import { PunchCard } from "@/components/PunchCard";
import { api, fmtKLTime, todayKL } from "@/lib/api";
import { useLang } from "@/lib/i18n";

// "YYYY-MM-DD" + 1 calendar day (pure string math, no timezone surprises)
const nextDay = (day) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

/* ---------------- Shift detail dialog (Batch 31) ----------------------------
   Click a report cell to open the underlying clock-in / clock-out punch
   records — the SAME PunchCard used in All Punches, including Flag, Override
   and Edit punch. Edits reuse the existing audited endpoints, so originals
   are preserved and the grid refreshes automatically after a change. */
const ShiftDetailDialog = ({ detail, onClose }) => {
  const { t } = useLang();
  const queryClient = useQueryClient();
  const ids = detail ? [detail.clockInPunchId, detail.clockOutPunchId].filter(Boolean) : [];
  // manual entry form state (supervisor adds a forgotten clock in/out)
  const [addType, setAddType] = useState(null); // "clock_in" | "clock_out" | null
  const [addTime, setAddTime] = useState("");
  const [addSite, setAddSite] = useState("");
  const [addNote, setAddNote] = useState("");
  const [addFiles, setAddFiles] = useState([]); // evidence pictures (Batch 33)
  // Batch 35: blank day -> add a full shift (clock-in + clock-out) in one go
  const isEmptyDay = Boolean(detail?.empty);
  const [pairOpen, setPairOpen] = useState(false);
  const [pairShift, setPairShift] = useState("");
  const [pairIn, setPairIn] = useState("");
  const [pairOut, setPairOut] = useState("");
  const [pairSite, setPairSite] = useState("");
  const [pairNote, setPairNote] = useState("");
  const [pairFiles, setPairFiles] = useState([]);

  const { data, isLoading } = useQuery({
    queryKey: ["report-shift-detail", ids.join(",")],
    queryFn: async () => (await api.get("/punches", { params: { ids: ids.join(",") } })).data,
    enabled: ids.length > 0,
  });
  const { data: shiftData } = useQuery({
    queryKey: ["shifts"],
    queryFn: async () => (await api.get("/shifts")).data,
    enabled: isEmptyDay,
  });
  const activeShifts = (shiftData?.items || []).filter((sh) => sh.isActive);

  // reset the full-shift form whenever a (different) empty day is opened
  React.useEffect(() => {
    if (!detail) return;
    setPairOpen(isEmptyDay);
    setPairShift("");
    setPairIn(detail.day ? `${detail.day}T10:00` : "");
    setPairOut(detail.day ? `${detail.day}T22:00` : "");
    setPairSite(detail.defaultSiteId || "");
    setPairNote("");
    setPairFiles([]);
    setAddType(null);
  }, [detail, isEmptyDay]);

  const applyShift = (shiftId) => {
    setPairShift(shiftId);
    const sh = activeShifts.find((x) => x.id === shiftId);
    if (!sh || !detail?.day) return;
    setPairIn(`${detail.day}T${sh.startTime}`);
    // overnight shift: clock-out lands on the next calendar day
    const outDay = sh.overnight || sh.endTime <= sh.startTime ? nextDay(detail.day) : detail.day;
    setPairOut(`${outDay}T${sh.endTime}`);
  };

  const pairMutation = useMutation({
    mutationFn: async (payload) => {
      const created = (await api.post("/punches/manual-pair", payload)).data;
      // evidence pictures are attached to BOTH entries so either record
      // shows the supporting document on review (Batch 33 flow)
      if (pairFiles.length > 0) {
        for (const id of [created.clockIn.id, created.clockOut.id]) {
          const fd = new FormData();
          pairFiles.forEach((f) => fd.append("documents", f));
          await api.post(`/punches/${id}/documents`, fd);
        }
      }
      return created;
    },
    onSuccess: () => {
      toast.success(t("fullShiftAdded"));
      queryClient.invalidateQueries();
      onClose();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });
  // chronological: clock-in first
  const items = [...(data?.items || [])].sort((a, b) =>
    new Date(a.serverTimestamp) - new Date(b.serverTimestamp));

  const { data: locData } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data,
    enabled: detail !== null,
  });
  const activeLocations = (locData?.items || []).filter((l) => l.isActive);

  const reviewMutation = useMutation({
    mutationFn: async ({ id, body }) => (await api.post(`/punches/${id}/review`, body)).data,
    onSuccess: () => {
      toast.success(t("done"));
      queryClient.invalidateQueries();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  // Batch 39: reviewers can delete a clock-in / clock-out straight from the
  // grid drill-down. The report queries are invalidated so the day cell (or
  // the whole staff row, if this was their only punch) disappears at once.
  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/punches/${id}`)).data,
    onSuccess: () => {
      toast.success(t("punchDeleted"));
      queryClient.invalidateQueries();
      if (items.length <= 1) onClose();  // nothing left to show in this cell
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const openAdd = (type) => {
    setAddType(type);
    // sensible default: the cell's own day at a typical boundary time
    setAddTime(`${detail?.day || todayKL()}T${type === "clock_in" ? "10:00" : "22:00"}`);
    setAddSite(items[0]?.siteId || detail?.defaultSiteId || "");
    setAddNote("");
    setAddFiles([]);
  };

  const addMutation = useMutation({
    mutationFn: async (payload) => {
      const created = (await api.post("/punches/manual", payload)).data;
      // optional evidence pictures chosen at creation time (Batch 33)
      if (addFiles.length > 0) {
        const fd = new FormData();
        addFiles.forEach((f) => fd.append("documents", f));
        await api.post(`/punches/${created.id}/documents`, fd);
      }
      return created;
    },
    onSuccess: () => {
      toast.success(t("manualEntryAdded"));
      queryClient.invalidateQueries();
      setAddType(null);
      onClose(); // the grid re-pairs sessions — reopen the cell to see the result
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  return (
    <Dialog open={detail !== null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="shift-detail-dialog"
        className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {detail?.userName} — {detail?.day}
            {detail?.shiftName ? ` · ${detail.shiftName}` : ""}
            {detail?.hours != null ? ` · ${detail.hours}h` : ""}
          </DialogTitle>
        </DialogHeader>
        {isLoading && <Skeleton className="h-48 w-full rounded-xl" />}
        {isEmptyDay && (
          <div data-testid="empty-day-notice"
            className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <CalendarPlus className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-semibold">{t("noAttendanceDay")}</p>
              <p className="text-xs">{t("noAttendanceDayHint")}</p>
            </div>
          </div>
        )}
        {!isLoading && !isEmptyDay && items.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">{t("noResults")}</p>
        )}

        {/* ---- Batch 35: add a FULL shift (clock-in + clock-out) on a blank day ---- */}
        {isEmptyDay && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
            {!pairOpen ? (
              <Button size="sm" data-testid="add-full-shift-button" onClick={() => setPairOpen(true)}>
                <CalendarPlus className="mr-1.5 h-4 w-4" /> {t("addFullShift")}
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-semibold">{t("addFullShift")} — {detail?.userName}</p>
                <Select value={pairShift || undefined} onValueChange={applyShift}>
                  <SelectTrigger data-testid="full-shift-shift-select" className="h-9">
                    <SelectValue placeholder={t("pickShiftToPrefill")} />
                  </SelectTrigger>
                  <SelectContent>
                    {activeShifts.map((sh) => (
                      <SelectItem key={sh.id} value={sh.id}>
                        {sh.name} · {sh.startTime}–{sh.endTime}{sh.overnight ? " (+1)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="full-shift-in">{t("inTime")}</label>
                    <Input id="full-shift-in" type="datetime-local" value={pairIn}
                      onChange={(e) => setPairIn(e.target.value)}
                      data-testid="full-shift-in-input" className="mt-1 h-9" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="full-shift-out">{t("outTime")}</label>
                    <Input id="full-shift-out" type="datetime-local" value={pairOut}
                      onChange={(e) => setPairOut(e.target.value)}
                      data-testid="full-shift-out-input" className="mt-1 h-9" />
                  </div>
                </div>
                <Select value={pairSite || undefined} onValueChange={setPairSite}>
                  <SelectTrigger data-testid="full-shift-location-select" className="h-9">
                    <SelectValue placeholder={t("location")} />
                  </SelectTrigger>
                  <SelectContent>
                    {activeLocations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input value={pairNote} onChange={(e) => setPairNote(e.target.value)}
                  placeholder={t("note")} data-testid="full-shift-note-input" className="h-9" />
                <div>
                  <label className="text-xs text-muted-foreground" htmlFor="full-shift-evidence-input">
                    {t("evidenceOptional")}
                  </label>
                  <Input id="full-shift-evidence-input" type="file" accept="image/*" multiple
                    onChange={(e) => setPairFiles(Array.from(e.target.files || []))}
                    data-testid="full-shift-evidence-input"
                    className="mt-1 h-9 cursor-pointer file:mr-2 file:text-xs" />
                  {pairFiles.length > 0 && (
                    <p className="mt-1 text-xs text-muted-foreground" data-testid="full-shift-evidence-count">
                      {pairFiles.length} {t("picturesSelected")} · {t("evidenceAttachedBoth")}
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setPairOpen(false)}>
                    {t("cancel")}
                  </Button>
                  <Button size="sm" data-testid="full-shift-save-button"
                    disabled={!pairIn || !pairOut || pairMutation.isPending}
                    onClick={() => pairMutation.mutate({
                      userId: detail.userId, clockIn: pairIn, clockOut: pairOut,
                      siteId: pairSite || null, note: pairNote || null,
                    })}>
                    {pairMutation.isPending ? t("saving") : t("saveFullShift")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="space-y-3">
          {items.map((p) => (
            <PunchCard
              key={p.id}
              punch={p}
              busy={reviewMutation.isPending || deleteMutation.isPending}
              onReview={(id, body) => reviewMutation.mutateAsync({ id, body })}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
        </div>

        {/* ---- add a missing clock in/out (manual, no photo, audited) ---- */}
        <div className="rounded-lg border border-dashed border-border p-3">
          {addType === null ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">{t("missingPunchHint")}</span>
              <Button size="sm" variant="outline" data-testid="add-clock-in-button"
                onClick={() => openAdd("clock_in")}>
                + {t("addClockIn")}
              </Button>
              <Button size="sm" variant="outline" data-testid="add-clock-out-button"
                onClick={() => openAdd("clock_out")}>
                + {t("addClockOut")}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-semibold">
                {addType === "clock_in" ? t("addClockIn") : t("addClockOut")} — {detail?.userName}
              </p>
              <Input type="datetime-local" value={addTime}
                onChange={(e) => setAddTime(e.target.value)}
                data-testid="manual-punch-time-input" className="h-9" />
              <Select value={addSite || undefined} onValueChange={setAddSite}>
                <SelectTrigger data-testid="manual-punch-location-select" className="h-9">
                  <SelectValue placeholder={t("location")} />
                </SelectTrigger>
                <SelectContent>
                  {activeLocations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input value={addNote} onChange={(e) => setAddNote(e.target.value)}
                placeholder={t("note")} data-testid="manual-punch-note-input" className="h-9" />
              <div>
                <label className="text-xs text-muted-foreground" htmlFor="manual-evidence-input">
                  {t("evidenceOptional")}
                </label>
                <Input id="manual-evidence-input" type="file" accept="image/*" multiple
                  onChange={(e) => setAddFiles(Array.from(e.target.files || []))}
                  data-testid="manual-punch-evidence-input"
                  className="mt-1 h-9 cursor-pointer file:mr-2 file:text-xs" />
                {addFiles.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="manual-punch-evidence-count">
                    {addFiles.length} {t("picturesSelected")}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAddType(null)}>
                  {t("cancel")}
                </Button>
                <Button size="sm" data-testid="manual-punch-save-button"
                  disabled={!addTime || addMutation.isPending}
                  onClick={() => addMutation.mutate({
                    userId: detail.userId, punchType: addType,
                    serverTimestamp: addTime, siteId: addSite || null,
                    note: addNote || null,
                  })}>
                  {t("save")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------- Hours summary (per staff) ------------------------
   Also reused by the By-location tab (Batch 29): pass locationId/locationName
   to scope the same grid to a single location. */
const HoursSummary = ({ locationId = "", locationName = "" }) => {
  const { t, lang } = useLang();
  const [periodType, setPeriodType] = useState("month");
  const [month, setMonth] = useState(() => todayKL().slice(0, 7));
  const [weekStart, setWeekStart] = useState(() => todayKL(-6));

  const params = periodType === "month" ? { month } : { weekStart };
  if (locationId) params.locationId = locationId;
  const { data, isLoading } = useQuery({
    queryKey: ["report", periodType, month, weekStart, locationId],
    queryFn: async () => (await api.get("/reports/summary", { params })).data,
  });

  const rowsWithWork = useMemo(
    () => (data?.rows || []).filter((r) => r.daysWorked > 0),
    [data],
  );
  const [includeIdle, setIncludeIdle] = useState(false);
  const rows = includeIdle ? (data?.rows || []) : rowsWithWork;
  // which cell's punch records are open for review/editing
  const [detail, setDetail] = useState(null);

  // Batch 38: monthly PDF with the full evidence trail (summary + sign-off,
  // supervisor activity trail, supporting documents). Downloaded through the
  // authenticated API client, then saved via a temporary object URL.
  const [exporting, setExporting] = useState(null); // "pdf" | "xlsx" | null
  // Shared download helper for the monthly exports (PDF with evidence trail,
  // Excel summary grid — Batch 41). Both go through the authenticated API
  // client and are saved via a temporary object URL.
  const exportFile = async (kind) => {
    const isPdf = kind === "pdf";
    setExporting(kind);
    try {
      const res = await api.get(isPdf ? "/reports/summary.pdf" : "/reports/summary.xlsx", {
        params: { month, locationId: locationId || undefined, includeIdle },
        responseType: "blob",
        timeout: 120000,
      });
      const safeSite = (locationName || "All_locations").replace(/[^A-Za-z0-9]+/g, "_");
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RajinKerja_Monthly_${month}_${safeSite}.${isPdf ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success(t(isPdf ? "pdfExported" : "xlsxExported"));
    } catch (err) {
      toast.error(t(isPdf ? "pdfExportFailed" : "xlsxExportFailed"));
    } finally {
      setExporting(null);
    }
  };
  const exportPdf = () => exportFile("pdf");
  const exportXlsx = () => exportFile("xlsx");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <Select value={periodType} onValueChange={setPeriodType}>
          <SelectTrigger data-testid="report-period-type" className="h-9 w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">{t("monthly")}</SelectItem>
            <SelectItem value="week">{t("weekly")}</SelectItem>
          </SelectContent>
        </Select>
        {periodType === "month" ? (
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            data-testid="report-month-input" className="h-9 w-auto" />
        ) : (
          <Input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)}
            data-testid="report-week-input" className="h-9 w-auto" />
        )}
        {/* location-scoped reports only list people who worked there, so the
            idle-staff scope select is meaningless in that mode */}
        {!locationId && (
        <Select value={includeIdle ? "all" : "worked"} onValueChange={(v) => setIncludeIdle(v === "all")}>
          <SelectTrigger data-testid="report-staff-scope" className="h-9 w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="worked">{t("staffWhoWorked")}</SelectItem>
            <SelectItem value="all">{t("allStaff")}</SelectItem>
          </SelectContent>
        </Select>
        )}
        {periodType === "month" ? (
          <>
            <Button onClick={exportPdf} disabled={exporting !== null || isLoading}
              data-testid="report-export-pdf-button">
              {exporting === "pdf"
                ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                : <FileDown className="mr-2 h-4 w-4" />}
              {exporting === "pdf" ? t("pdfExporting") : t("exportPdf")}
            </Button>
            <Button onClick={exportXlsx} disabled={exporting !== null || isLoading}
              variant="outline" data-testid="report-export-xlsx-button">
              {exporting === "xlsx"
                ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                : <FileSpreadsheet className="mr-2 h-4 w-4" />}
              {exporting === "xlsx" ? t("xlsxExporting") : t("exportXlsx")}
            </Button>
          </>
        ) : (
          <Button onClick={() => window.print()} data-testid="report-print-button" variant="outline">
            <Printer className="mr-2 h-4 w-4" /> {t("printReportShort")}
          </Button>
        )}
      </div>
      {periodType === "month" && (
        <p className="text-xs text-muted-foreground print:hidden" data-testid="report-export-pdf-hint">
          {t("exportPdfHint")}
        </p>
      )}

      {isLoading && <Skeleton className="h-72 w-full rounded-xl" />}

      {data && (
        <div id="report-print-area" className="space-y-4">
          {/* print header */}
          <div className="hidden print:block">
            <h1 className="text-lg font-semibold">
              RajinKerja — {periodType === "month" ? t("monthly") : t("weekly")} {t("attendanceSummary")}
              {locationName ? ` — ${locationName}` : ""} — {data.period}
            </h1>
            <p className="text-xs">{t("generatedAt")}: {new Date(data.generatedAt).toLocaleString(lang === "bm" ? "ms-MY" : "en-MY")}</p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border bg-card print:overflow-visible print:rounded-none print:border-0">
            <table data-testid="report-table" className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="sticky left-0 bg-card p-2 text-left font-semibold">{t("staff")}</th>
                  {data.dates.map((d) => (
                    <th key={d} className="p-1 text-center font-medium text-muted-foreground">{d.slice(8)}</th>
                  ))}
                  <th className="p-2 text-right font-semibold">{t("hours")}</th>
                  <th className="p-2 text-right font-semibold">{t("daysWorkedShort")}</th>
                  <th className="p-2 text-right font-semibold">{t("lateMinutesShort")}</th>
                  <th className="p-1 text-center font-semibold"><Sun className="inline h-3.5 w-3.5 text-amber-600" /></th>
                  <th className="p-1 text-center font-semibold"><Moon className="inline h-3.5 w-3.5 text-indigo-600" /></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={data.dates.length + 6} className="p-8 text-center text-muted-foreground">{t("noResults")}</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.userId} data-testid="report-row" className="border-b border-border last:border-0">
                    <td className="sticky left-0 whitespace-nowrap bg-card p-2 font-medium">
                      {r.userName}
                      <span className="ml-1 text-[10px] text-muted-foreground">{r.employeeId}</span>
                    </td>
                    {data.dates.map((d) => {
                      const e = r.entries[d];
                      if (!e) {
                        // Batch 35: a blank day is clickable (unless in the
                        // future) so a reviewer can repair it — e.g. the face
                        // engine was down all day and nobody could punch.
                        const future = d > todayKL();
                        return (
                          <td key={d} className="p-0.5 text-center">
                            <button
                              type="button"
                              data-testid="report-empty-cell"
                              disabled={future}
                              title={future ? "" : t("emptyDayClickHint")}
                              onClick={() => !future && setDetail({
                                userId: r.userId, userName: r.userName, day: d,
                                empty: true, defaultSiteId: locationId || "",
                              })}
                              className={`group inline-flex min-w-[26px] items-center justify-center rounded px-1 py-1 text-[11px] leading-tight text-muted-foreground/40 transition-colors duration-150 ${
                                future ? "cursor-default"
                                  : "cursor-pointer hover:bg-primary/10 hover:text-primary focus-visible:ring-2 focus-visible:ring-primary"} print:transition-none`}
                            >
                              <span className="group-hover:hidden">·</span>
                              <span className="hidden font-semibold group-hover:inline print:hidden">+</span>
                            </button>
                          </td>
                        );
                      }
                      const isDay = e.kind === "day";
                      const clickable = Boolean(e.clockInPunchId || e.clockOutPunchId);
                      return (
                        <td key={d} className="p-0.5 text-center">
                          <button
                            type="button"
                            data-testid="report-cell"
                            disabled={!clickable}
                            onClick={() => clickable && setDetail({
                              userId: r.userId, userName: r.userName, day: d,
                              shiftName: e.shiftName, hours: e.hours,
                              clockInPunchId: e.clockInPunchId,
                              clockOutPunchId: e.clockOutPunchId,
                            })}
                            title={`${e.shiftName || ""} ${e.clockIn ? fmtKLTime(e.clockIn) : ""}–${e.clockOut ? fmtKLTime(e.clockOut) : ""}${e.hours != null ? ` · ${e.hours}h` : ""}`}
                            className={`inline-flex min-w-[26px] flex-col items-center rounded px-1 py-0.5 font-mono text-[10px] leading-tight transition-transform duration-150 ${
                              isDay ? "bg-amber-50 text-amber-900" : "bg-indigo-50 text-indigo-900"
                            } ${clickable
                              ? "cursor-pointer hover:scale-110 hover:ring-2 hover:ring-primary/50 focus-visible:ring-2 focus-visible:ring-primary"
                              : "cursor-default"} print:transition-none`}
                          >
                            <span className="font-semibold">{isDay ? "D" : "N"}</span>
                            <span>{e.hours != null ? e.hours : "–"}</span>
                          </button>
                        </td>
                      );
                    })}
                    <td className="p-2 text-right font-mono font-semibold tabular-nums">{r.totalHours}</td>
                    <td className="p-2 text-right font-mono tabular-nums">{r.daysWorked}</td>
                    <td
                      data-testid="report-late-minutes"
                      className={`p-2 text-right font-mono tabular-nums ${r.totalLateMinutes > 0 ? "font-semibold text-red-700" : "text-muted-foreground"}`}
                    >
                      {r.totalLateMinutes > 0 ? r.totalLateMinutes : "–"}
                    </td>
                    <td className="p-1 text-center font-mono tabular-nums">{r.dayShiftDays}</td>
                    <td className="p-1 text-center font-mono tabular-nums">{r.nightShiftDays}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-muted-foreground print:hidden">
            <Sun className="inline h-3 w-3 text-amber-600" /> D = {t("dayKind")} ·{" "}
            <Moon className="inline h-3 w-3 text-indigo-600" /> N = {t("nightKind")} · {t("cellHint")} · {t("cellClickHint")} · {t("emptyDayClickHint")}
          </p>
        </div>
      )}

      <ShiftDetailDialog detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
};

/* ------------------- Monthly manpower report by location --------------------
   Batch 29: a single selected location now renders the SAME hour-summary grid
   (scoped via locationId). The all-locations overview is unchanged. */
const LocationReport = () => {
  const { t, lang } = useLang();
  const [month, setMonth] = useState(() => todayKL().slice(0, 7));
  const [locationId, setLocationId] = useState("");

  const { data: locData } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data,
  });
  const locations = locData?.items || [];
  const selectedLocation = locations.find((l) => l.id === locationId);

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ["locations-overview", month],
    queryFn: async () => (await api.get("/reports/overview", { params: { month } })).data,
    enabled: locationId === "__all__",
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <Select value={locationId || undefined} onValueChange={setLocationId}>
          <SelectTrigger data-testid="location-report-select" className="h-9 w-56">
            <SelectValue placeholder={t("selectLocation")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t("allLocationsOverview")}</SelectItem>
            {locations.map((l) => (
              <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {locationId === "__all__" && (
          <>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              data-testid="location-report-month-input" className="h-9 w-auto" />
            <Button onClick={() => window.print()} data-testid="location-report-print-button"
              variant="outline" disabled={!overview}>
              <Printer className="mr-2 h-4 w-4" /> {t("printReportShort")}
            </Button>
          </>
        )}
      </div>

      {!locationId && (
        <div data-testid="location-report-empty" className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border p-10 text-center">
          <MapPin className="h-7 w-7 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("pickLocationHint")}</p>
        </div>
      )}

      {/* single location: identical hour-summary grid, scoped to this location */}
      {locationId && locationId !== "__all__" && (
        <HoursSummary key={locationId} locationId={locationId}
          locationName={selectedLocation?.name || ""} />
      )}

      {locationId === "__all__" && overviewLoading && <Skeleton className="h-72 w-full rounded-xl" />}

      {locationId === "__all__" && overview && (
        <div id="report-print-area" className="space-y-3">
          <div className="hidden print:block">
            <h1 className="text-base font-semibold">
              RajinKerja — {t("allLocationsOverview")} — {overview.month}
            </h1>
            <p className="text-[10px]">{t("generatedAt")}: {new Date(overview.generatedAt).toLocaleString(lang === "bm" ? "ms-MY" : "en-MY")}</p>
          </div>
          <div className="overflow-hidden rounded-xl border border-border bg-card print:rounded-none print:border-0">
            <table data-testid="overview-table" className="w-full border-collapse text-sm print:text-[10px] print:leading-tight">
              <thead>
                <tr className="border-b border-border">
                  <th className="p-2 text-left font-semibold print:p-0.5">{t("locationLabel")}</th>
                  <th className="w-24 p-2 text-center font-semibold print:p-0.5">{t("daysActive")}</th>
                  <th className="w-24 p-2 text-center font-semibold print:p-0.5">{t("manDays")}</th>
                  <th className="w-24 p-2 text-right font-semibold print:p-0.5">{t("hours")}</th>
                </tr>
              </thead>
              <tbody>
                {overview.rows.length === 0 && (
                  <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">{t("noResults")}</td></tr>
                )}
                {overview.rows.map((r) => (
                  <tr key={r.locationId} data-testid="overview-row"
                    className={`border-b border-border last:border-0 ${r.manDays === 0 ? "text-muted-foreground/60" : ""}`}>
                    <td className="p-2 font-medium print:p-0.5">
                      <span className="inline-flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 text-primary print:hidden" /> {r.locationName}
                        {!r.isActive && <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">{t("inactiveLabel")}</span>}
                      </span>
                    </td>
                    <td className="p-2 text-center font-mono tabular-nums print:p-0.5">{r.daysActive || "–"}</td>
                    <td className="p-2 text-center font-mono tabular-nums print:p-0.5">{r.manDays || "–"}</td>
                    <td className="p-2 text-right font-mono tabular-nums print:p-0.5">{r.totalHours || "–"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-foreground/30 font-semibold">
                  <td className="p-2 print:p-0.5">{t("monthTotal")}</td>
                  <td className="p-2 print:p-0.5" />
                  <td data-testid="overview-total-man-days" className="p-2 text-center font-mono tabular-nums print:p-0.5">{overview.totalManDays}</td>
                  <td data-testid="overview-total-hours" className="p-2 text-right font-mono tabular-nums print:p-0.5">{overview.totalHours}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/* --------------------------------- Page ------------------------------------ */
export default function ReportsPage() {
  const { t } = useLang();
  const [tab, setTab] = useState("summary");

  return (
    <AppShell>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <h1 className="text-xl font-semibold tracking-tight">{t("reports")}</h1>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="print:hidden">
            <TabsTrigger value="summary" data-testid="reports-tab-summary">{t("hoursSummaryTab")}</TabsTrigger>
            <TabsTrigger value="location" data-testid="reports-tab-location">{t("byLocationTab")}</TabsTrigger>
          </TabsList>
          <TabsContent value="summary" className="mt-4">
            <HoursSummary />
          </TabsContent>
          <TabsContent value="location" className="mt-4">
            <LocationReport />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
